// 로봇 상태 전이표 — apps/api 와 apps/windows 의 공유 정전 소스.
//
// round11 요구2 — 종전엔 이 표가 apps/api/src/robots/state-machine 안에만
// 있어서 편집 폼의 상태 select 가 6개 상태를 전부 노출했다. 실제로 갈 수 없는
// 상태를 고르면 저장 시 422 로 거부되고 **PATCH 전체가 롤백**돼 같이 고친 SN·
// 제조월·비고까지 사라졌다("그냥 안 먹네"). 표를 shared 로 올려 폼이 같은
// 규칙으로 선택지를 좁힌다. 서버의 assertTransitionAllowed 는 그대로 유지
// (클라 필터는 UX, 서버 가드는 계약).
import { RobotStatus, VerificationStatus } from './enums.js';
// Robot status SM (plan §3.3 — 03-workflows-and-state-machines.md).
//
// Canonical transitions table. Each key is the current status; the
// corresponding Set is every status reachable via a single user-
// initiated transition. Same-state entries are included when the
// plan allows a self-loop with side effects handled by a specific
// transition (SM-04a IN_AS → IN_AS QC re-loop, SM-06 DEPLOYED →
// DEPLOYED store reassignment).
//
// RETIRED is terminal: no outbound transitions at this layer. The
// "reverse retirement" escape hatch (SM-11 reversal) is plan §3.3
// v1.2+ and belongs on its own dedicated endpoint — **not** inside
// this generic PATCH flow.
//
// Forbidden by design (explicit exclusion from the table):
//   • IN_PRODUCTION → DEPLOYED   — must go through READY_TO_SHIP
//   • IN_PRODUCTION → IN_AS      — no valid scenario (not-yet-shipped
//                                   hardware can't be in AS)
//   • READY_TO_SHIP → IN_PRODUCTION — manufacturing is one-way
//   • DEPLOYED → IN_PRODUCTION   — prod → customer → prod not a path
//   • RETIRED → *                — terminal
// round10 요구2 — RETIREMENT_PLANNED('폐기 예정')는 RETIRED('폐기 완료')의 전 단계.
// RETIRED 가 현재 도달 가능한 모든 상태에서 '폐기 예정'으로도 갈 수 있게 하고
// (계획 D3: 예정=매니저+ 권한), 예정 상태에서는 완료(RETIRED, ADMIN)로 확정하거나
// 보관(IN_STORAGE)으로 되돌릴 수 있다. RETIRED 는 여전히 terminal.
export const ROBOT_STATUS_TRANSITIONS = {
    [RobotStatus.IN_PRODUCTION]: new Set([
        RobotStatus.READY_TO_SHIP, // SM-02
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED, // SM-11a R&D scrap
    ]),
    [RobotStatus.READY_TO_SHIP]: new Set([
        RobotStatus.INSTALL_PENDING, // S2 §9.1 — 필드 설치 일정 확정 시 '설치 대기'
        RobotStatus.DEPLOYED, // SM-03 (설치 대기 단계 생략 직배치 — 호환 유지)
        RobotStatus.IN_AS, // SM-10 pre-ship defect
        RobotStatus.OTHER, // 2026-09-18 — 행사·인증·테스트·사내 배치
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED, // SM-11b warehouse scrap
    ]),
    // S2 §9.1 — '설치 대기'. 제조 할당+필드 설치일정 확정 후 ~ operator-pwa 인수 전.
    // 인수(Acceptance) → DEPLOYED. 취소/결함 시 출고 대기/정비로 복귀.
    [RobotStatus.INSTALL_PENDING]: new Set([
        RobotStatus.DEPLOYED, // 인수증/사진 업로드 → 운영중 (Acceptance finalize)
        RobotStatus.READY_TO_SHIP, // 설치 취소 → 출고 대기 복귀
        RobotStatus.IN_AS, // 설치 전 결함 발견
        RobotStatus.OTHER, // 2026-09-18 — 설치 취소 후 행사·인증으로 돌림
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED, // scrap
    ]),
    [RobotStatus.DEPLOYED]: new Set([
        RobotStatus.DEPLOYED, // SM-06 store reassignment
        RobotStatus.IN_AS, // SM-04
        RobotStatus.READY_TO_SHIP, // SM-07 recall
        RobotStatus.OTHER, // 2026-09-18 — 잘못 '운영중' 으로 잡힌 건의 정정
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED, // SM-11c field scrap
    ]),
    [RobotStatus.IN_AS]: new Set([
        RobotStatus.IN_AS, // SM-04a QC fail re-loop
        RobotStatus.DEPLOYED, // SM-05 repair complete → back to field
        RobotStatus.READY_TO_SHIP, // SM-05 repair complete → back to warehouse
        RobotStatus.OTHER, // 2026-09-18 — 수리 끝나고 바로 행사·인증으로
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED, // SM-11d AS diag scrap / SM-04b 3 QC fails
    ]),
    // round10 요구2 — 폐기 예정: 완료(RETIRED, ADMIN) 확정 또는 보관(IN_STORAGE)으로 되돌리기.
    [RobotStatus.RETIREMENT_PLANNED]: new Set([
        RobotStatus.RETIRED, // 상각 완료 → 폐기 완료
        RobotStatus.IN_STORAGE, // 예정 취소 → 보관 복귀
    ]),
    [RobotStatus.RETIRED]: new Set(),
    // batch-15 §5.1 — IN_STORAGE 는 회수 후 보관 중. READY_TO_SHIP 또는 IN_AS 로 재진입 가능.
    [RobotStatus.IN_STORAGE]: new Set([
        RobotStatus.READY_TO_SHIP,
        RobotStatus.IN_AS,
        RobotStatus.OTHER, // 2026-09-18 — 보관 중이던 로봇을 행사·인증으로
        RobotStatus.RETIREMENT_PLANNED, // round10 — 폐기 예정
        RobotStatus.RETIRED,
    ]),
    // 2026-09-18 — '기타': 행사·인증·테스트·사내로 나가 있는 로봇.
    //
    // 나오는 길의 정본은 **회수**다(`resolveReturnTransition`): 고객 아닌 배치의
    // 회수는 처분과 무관하게 출고 대기 + 창고로 돌아온다. 나머지는 정정·수리 경로.
    //
    // 자기 자신(OTHER → OTHER)을 포함한다 — 행사에서 바로 다음 행사로 옮기는 것이
    // 운영중의 매장 재배치(SM-06)와 같은 모양이다.
    [RobotStatus.OTHER]: new Set([
        RobotStatus.OTHER, // 행사 → 다음 행사 (SM-06 과 같은 꼴)
        RobotStatus.READY_TO_SHIP, // 회수 — 정본 경로
        RobotStatus.IN_AS, // 행사장에서 고장
        RobotStatus.DEPLOYED, // 그대로 고객 배치로 전환
        RobotStatus.IN_STORAGE, // 창고로 들여 보관
        RobotStatus.RETIREMENT_PLANNED,
        RobotStatus.RETIRED,
    ]),
};
/**
 * **현장에 나가 있는 로봇의 상태 — 정본 한 벌.**
 *
 * 2026-09-18 '기타' 신설 전에는 나가 있는 로봇이 전부 '운영중(DEPLOYED)' 이었다.
 * 그래서 서버 곳곳이 "나가 있다" 를 `status === DEPLOYED` 로 적어 두었다 —
 * 회수 뒤 남은 대수(고객사 자동 종료), 활성 고객사 목록, 기존고객사 판정, 부품 리콜
 * 대상, 고객사 파기 복원.
 *
 * '기타' 가 생기면서 그 조건들은 **말없이 "고객 배치만" 으로 좁혀졌다.** 행사·파일럿
 * 로봇이 매장에 남아 있어도 0대로 세어져, 마지막 고객 로봇을 회수하는 순간 고객사가
 * 자동 종료됐다(2026-09-23 배포 전 점검에서 발견 — 계획서엔 "같은 배포에 넣을 것" 으로
 * 적혀 있었는데 구현에서 빠졌다).
 *
 * ⚠ "나가 있다" 를 뜻하는 자리는 `DEPLOYED` 를 직접 쓰지 말고 이것을 쓸 것.
 *   반대로 "고객사에서 운영 중" 을 뜻하는 자리(KPI 의 운영중 카드)는 DEPLOYED 가 맞다 —
 *   둘을 섞지 말 것.
 */
export const ROBOT_OUT_IN_FIELD_STATUSES = [
    RobotStatus.DEPLOYED,
    RobotStatus.OTHER,
];
/** 이 상태의 로봇이 현장에 나가 있는가(고객 운영이든 행사·인증·테스트든). */
export function isRobotOutInField(status) {
    return ROBOT_OUT_IN_FIELD_STATUSES.includes(status);
}
/**
 * INV-03 (plan §03 §2): PHANTOM robots can't be shipped or deployed.
 * The same guard also runs in DeploymentsService.create() so it
 * applies whether the caller changes status via PATCH /robots/:id
 * or by creating a Deployment.
 */
export function isVerificationStatusAllowedForTransition(to, verificationStatus) {
    if (verificationStatus !== VerificationStatus.PHANTOM)
        return true;
    return (to !== RobotStatus.READY_TO_SHIP &&
        to !== RobotStatus.INSTALL_PENDING && // S2 §9.1 — 설치 대기도 물리 검증 선행
        to !== RobotStatus.DEPLOYED &&
        // 2026-09-18 — '기타' 도 막는다. 실물이 확인 안 된 로봇(유령)을 행사장·
        // 인증기관으로 보낼 수는 없다. 나가는 길인 이상 운영중과 같은 문턱이다.
        to !== RobotStatus.OTHER);
}
/**
 * Pure predicate: does the transition table allow from → to?
 * Role gating is a separate check (see isRoleAllowedForTransition).
 */
export function canTransitionStatus(from, to) {
    return ROBOT_STATUS_TRANSITIONS[from]?.has(to) ?? false;
}
/**
 * 상태 전이의 **역할 가드**.
 *
 * ⚠ 권한 통합(오너 결정 2026-08-27) — 종전엔 '폐기 완료(RETIRED)'가 ADMIN 전용이었다
 *   (plan §3 INV-05: 회계 승인 토큰. ACCOUNTING_ADMIN 역할이 없어 ADMIN 으로 대체).
 *   오너가 회계·법적 4종을 포함해 전부 열기로 하면서 이 자리도 함께 열었다 —
 *   **여기를 안 열면 "버튼은 보이는데 저장만 403"** 이 된다. 실제로 이 저장소가
 *   반복해서 겪은 결함이고, 권한이 네 겹(서버 @Roles / CASL / 상태머신 / 화면)이라
 *   한 겹만 고치면 반드시 남는다.
 *
 *   되돌릴 수 없는 조작인 것은 그대로다. 안전망은 감사 장부다
 *   (deployment.return · robot.status-correction 이 값 diff 와 사유를 남긴다).
 *
 * 남은 경계는 읽기 전용(VIEWER)뿐이다.
 */
export function isRoleAllowedForTransition(to, role) {
    // 폐기 계열(상각·상각 예정) — 열람 전용 계정만 막는다.
    if (to === RobotStatus.RETIRED || to === RobotStatus.RETIREMENT_PLANNED) {
        return role !== 'VIEWER';
    }
    return true;
}
/**
 * round11 요구2 — 세 가드(전이표 / 역할 / 검증상태)를 한 번에 통과하는지.
 * 편집 폼의 상태 select 가 "실제로 갈 수 있는 상태만" 남기는 데 쓴다. 서버는
 * 같은 세 가드를 assertTransitionAllowed 로 다시 검사한다(클라 우회 방지).
 */
export function isTransitionSelectable(args) {
    return (canTransitionStatus(args.from, args.to) &&
        isRoleAllowedForTransition(args.to, args.role) &&
        isVerificationStatusAllowedForTransition(args.to, args.verificationStatus));
}
//# sourceMappingURL=robot-status.js.map